/**
 * Cooking unit conversion + smart display picking.
 *
 * Used by the shopping list to merge duplicate ingredients across
 * recipes whose units differ but are compatible (e.g. tsp + tbsp,
 * oz + lb, g + kg). Stays within a single measurement family —
 * we don't auto-convert imperial ↔ metric because cooks generally
 * want the unit they wrote down.
 */

import { formatAmount } from "./scaleAmount";

export type UnitFamily =
  | "volume_imperial"
  | "volume_metric"
  | "weight_imperial"
  | "weight_metric";

interface UnitDef {
  family: UnitFamily;
  // Conversion factor to the family's base unit (tsp / ml / oz / g).
  toBase: number;
}

// Synonyms collapse to the canonical key. Keep keys lowercase.
const UNIT_ALIASES: Record<string, string> = {
  tablespoon: "tbsp",
  tablespoons: "tbsp",
  tbsps: "tbsp",
  "tbsp.": "tbsp",
  teaspoon: "tsp",
  teaspoons: "tsp",
  tsps: "tsp",
  "tsp.": "tsp",
  c: "cup",
  cups: "cup",
  pounds: "lb",
  pound: "lb",
  lbs: "lb",
  "lb.": "lb",
  ounces: "oz",
  ounce: "oz",
  "oz.": "oz",
  "fluid ounce": "fl oz",
  "fluid ounces": "fl oz",
  floz: "fl oz",
  "fl. oz.": "fl oz",
  pints: "pint",
  pt: "pint",
  quarts: "quart",
  qt: "quart",
  gallons: "gallon",
  gal: "gallon",
  grams: "g",
  gram: "g",
  kilograms: "kg",
  kilogram: "kg",
  kgs: "kg",
  milliliters: "ml",
  milliliter: "ml",
  mls: "ml",
  liters: "l",
  liter: "l",
  litre: "l",
  litres: "l",
};

// All units we know about. Family + factor to base unit.
const UNITS: Record<string, UnitDef> = {
  // Volume — imperial, base = tsp
  tsp: { family: "volume_imperial", toBase: 1 },
  tbsp: { family: "volume_imperial", toBase: 3 },
  "fl oz": { family: "volume_imperial", toBase: 6 },
  cup: { family: "volume_imperial", toBase: 48 },
  pint: { family: "volume_imperial", toBase: 96 },
  quart: { family: "volume_imperial", toBase: 192 },
  gallon: { family: "volume_imperial", toBase: 768 },
  // Volume — metric, base = ml
  ml: { family: "volume_metric", toBase: 1 },
  l: { family: "volume_metric", toBase: 1000 },
  // Weight — imperial, base = oz
  oz: { family: "weight_imperial", toBase: 1 },
  lb: { family: "weight_imperial", toBase: 16 },
  // Weight — metric, base = g
  g: { family: "weight_metric", toBase: 1 },
  kg: { family: "weight_metric", toBase: 1000 },
};

// When summing, pick the largest unit in the family whose value is
// at least the threshold. Order matters — descending by toBase.
const DISPLAY_PREFERENCE: Record<
  UnitFamily,
  Array<{ unit: string; toBase: number; threshold: number }>
> = {
  volume_imperial: [
    { unit: "gallon", toBase: 768, threshold: 1 },
    { unit: "quart", toBase: 192, threshold: 1 },
    { unit: "cup", toBase: 48, threshold: 0.25 }, // 1/4 cup or more
    { unit: "tbsp", toBase: 3, threshold: 1 },
    { unit: "tsp", toBase: 1, threshold: 0 },
  ],
  volume_metric: [
    { unit: "l", toBase: 1000, threshold: 1 },
    { unit: "ml", toBase: 1, threshold: 0 },
  ],
  weight_imperial: [
    { unit: "lb", toBase: 16, threshold: 0.5 }, // 1/2 lb or more
    { unit: "oz", toBase: 1, threshold: 0 },
  ],
  weight_metric: [
    { unit: "kg", toBase: 1000, threshold: 1 },
    { unit: "g", toBase: 1, threshold: 0 },
  ],
};

/**
 * Map any user-written unit string to our canonical key (or "" if
 * empty / unrecognized). Recognized keys can be looked up in UNITS;
 * unrecognized keys still get a stable lowercased form so callers
 * can group by unit in the absence of a known conversion.
 */
export function normalizeUnit(raw: string | null | undefined): string {
  if (!raw) return "";
  const s = raw.trim().toLowerCase();
  if (!s) return "";
  return UNIT_ALIASES[s] ?? s;
}

/** Returns the unit family if the canonical unit is one we can convert. */
export function getUnitFamily(canonicalUnit: string): UnitFamily | null {
  return UNITS[canonicalUnit]?.family ?? null;
}

interface UnitPart {
  amount: number;
  unit: string; // canonical
}

/**
 * Try to combine a list of (amount, unit) pairs into one (amount, unit)
 * by converting through a shared base unit and picking the most readable
 * display unit. Returns null when:
 *   - any unit is unknown
 *   - parts span more than one family (e.g. cup + g, or cup + ml)
 *
 * The caller should fall back to showing each part separately.
 */
export function combineConvertibleAmounts(
  parts: UnitPart[],
): { amount: string; unit: string } | null {
  if (parts.length === 0) return null;

  let family: UnitFamily | null = null;
  let totalInBase = 0;

  for (const p of parts) {
    const def = UNITS[p.unit];
    if (!def) return null;
    if (family === null) family = def.family;
    else if (family !== def.family) return null;
    totalInBase += p.amount * def.toBase;
  }

  if (family === null || totalInBase <= 0) return null;

  const prefs = DISPLAY_PREFERENCE[family];
  for (const pref of prefs) {
    const value = totalInBase / pref.toBase;
    if (value >= pref.threshold) {
      return { amount: formatAmount(value), unit: pref.unit };
    }
  }

  // Fallback to base unit (last entry has threshold 0).
  const base = prefs[prefs.length - 1];
  return {
    amount: formatAmount(totalInBase / base.toBase),
    unit: base.unit,
  };
}
